import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TournamentTile } from 'components/SaloonLobby/GameTile';
import { DayMeta, Tournament } from 'components/SaloonLobby/types';
import { DAY_ROW_VARS, useCollapsingRow } from 'components/SaloonLobby/useCollapsingRow';
import { useDragScroll } from 'components/SaloonLobby/useDragScroll';

import styles from './TournamentDayCarousel.module.scss';

/**
 * Order within a single day column: pure ascending `startAt`, so the
 * tournament nearest to starting is at the top regardless of its status or
 * whether the player has registered. A 10:00 PM ANNOUNCED sits above an
 * 11:59 PM REGISTERING because 10 PM starts first, and that is what a
 * player scanning the day is looking for.
 *
 * The prior tier system (registered → in-progress → registering → announced
 * → other) was dropped intentionally per EN request: status tiers scrambled
 * the chronological read and pushed later tournaments above earlier ones.
 * Exported for unit testing.
 */
export const compareTournamentsInDay = (a: Tournament, b: Tournament): number => {
  return (a.startAt || 0) - (b.startAt || 0);
};

interface TournamentDayCarouselProps {
  days: DayMeta[];
  tournaments: Tournament[];
  onSelectTournament?: (id: string) => void;
  /**
   * What to say when the schedule has nothing to show. The lobby knows whether
   * the room is quiet or the filters are too tight; this component only sees
   * the filtered list, so it is told rather than guessing (see emptyCopy in
   * SaloonLobby.tsx). Defaults to the over-filtered line.
   */
  emptyCopy?: string;
  /**
   * Which column is TODAY, as a `YYYY-MM-DD` dayKey (owner, 2026-09-11). Marks
   * that chip the way a calendar does — quietly, and separately from whichever
   * day is selected. The lobby passes it because only it holds the engine
   * clock; absent, nothing is marked rather than "today" guessing wrong off the
   * device clock.
   */
  todayKey?: string;
  // EN-10064: tournament IDs the viewer can't register for (flagged opponent
  // registered) — drives the advisory "unavailable" marker on each tile.
  restrictedIds?: Set<string>;
  // Lobby Card Size preference — forwarded to each tournament tile.
  size?: 'compact' | 'large';
  // Day to select on mount. The lobby conditionally renders this component, so
  // leaving the Tournaments tab unmounts it and the selection would otherwise be
  // lost — the lobby remembers the last day and hands it back here, the same way
  // it remembers each tab's scroll position. Omitted (or no longer available)
  // falls back to the first visible day. See onDayChange.
  initialDay?: string;
  // Fires whenever the selected day changes, including the automatic seed/re-seed,
  // so the lobby's remembered day never drifts from what's actually selected.
  onDayChange?: (dayKey: string) => void;
}

// The selected day is seeded from `initialDay` and reported back via `onDayChange`
// rather than being purely local, so a round trip to another tab returns the
// player to the day they were reading (matching the restored scroll position).
/**
 * Where the carousel must scroll to bring a day's column to its leading edge.
 *
 * This used to be `node.offsetLeft - el.offsetLeft`, which silently ate the
 * lane's own `padding-left` (owner, 2026-09-10: on a phone the first column sat
 * hard against the screen edge while every other element kept the 12px viewport
 * inset). `offsetLeft` is measured from the offset PARENT, so the difference
 * carries the lane's padding whenever the lane is not that parent — desktop has
 * no horizontal padding on this lane and never showed it, the phone lane has
 * 14px and was scrolled by exactly that on mount.
 *
 * Measuring from live rects instead makes it independent of both the padding and
 * the offset parent: how far the day sits from the lane's content start, plus
 * where the lane is already scrolled to. Clamped at 0 so the FIRST day resolves
 * to "not scrolled" rather than a negative it would clamp to anyway.
 *
 * All values in CSS pixels; exported for its own unit test.
 */
export const dayScrollLeft = ({
  scrollLeft,
  laneLeft,
  dayLeft,
  padLeft
}: {
  scrollLeft: number;
  laneLeft: number;
  dayLeft: number;
  padLeft: number;
}): number => Math.max(0, scrollLeft + (dayLeft - laneLeft) - padLeft);

/**
 * What the day chips' row collapses by: its ground — the chips and the day gap
 * beneath them — which is the row's only child. Not the row itself, whose
 * static top padding (the gap under the panel) stays put so the chips clip at
 * the panel's edge; see .dayRow / .dayGround in the stylesheet.
 */
export const dayGroundOf = (row: HTMLElement): HTMLElement | null =>
  row.firstElementChild as HTMLElement | null;

/** The id of a day column's pinned head, which labels the column for assistive tech. */
export const dayHeadId = (dayKey: string): string => `tournament-day-head-${dayKey}`;

/** The heads' track transform that puts the heads over their columns at a given scrollLeft. */
export const headsShiftFor = (scrollLeft: number): string => `translateX(${-scrollLeft}px)`;

/** The x translation of a computed `transform`, px; null when there is none to read. */
export const parseTranslateX = (transform: string): number | null => {
  const m = transform.match(/^matrix(3d)?\((.+)\)$/);
  if (!m) return null;
  const parts = m[2].split(',').map((v) => parseFloat(v));
  const tx = m[1] ? parts[12] : parts[4];
  return Number.isFinite(tx) ? tx : null;
};

/**
 * Is the heads' track where the columns' scroll says it should be? Unreadable
 * (no transform yet, or a browser that reports none) counts as in step — only
 * a transform that can be read and disagrees is evidence.
 */
export const headsInStep = (track: HTMLElement, lane: HTMLElement): boolean => {
  const tx = parseTranslateX(getComputedStyle(track).transform);
  return tx === null || Math.abs(tx + lane.scrollLeft) <= 1;
};

const headsKeyframes = (overflow: number) => [
  { transform: 'translateX(0px)' },
  { transform: `translateX(${-overflow}px)` }
];

/**
 * Bind the heads' track to the carousel's scroll position on the compositor:
 * a Web Animations animation on a ScrollTimeline whose source IS the carousel,
 * so there is no timeline name for a browser to fail to resolve. (The first
 * cut declared the timeline in CSS and scoped its name on a common ancestor;
 * Safari 26 on the owner's iPhone accepted every property, resolved nothing,
 * ran the animation on the document timeline with a zero duration, and held
 * its last keyframe — the heads sat frozen at the final column, 2026-09-11.)
 * Null where the browser has no ScrollTimeline, and the caller mirrors instead.
 */
export const rideScrollTimeline = (
  track: HTMLElement,
  lane: HTMLElement,
  overflow: number
): Animation | null => {
  const ScrollTimelineCtor = (
    window as unknown as { ScrollTimeline?: new (o: unknown) => AnimationTimeline }
  ).ScrollTimeline;
  if (typeof ScrollTimelineCtor !== 'function' || typeof track.animate !== 'function') return null;
  try {
    const timeline = new ScrollTimelineCtor({ source: lane, axis: 'x' });
    // No duration: on a scroll timeline that is the whole scroll range. `fill:
    // both` so the last column's head holds at the very end of the scroll,
    // where the active interval would otherwise be over.
    // `timeline` is newer than this TypeScript's DOM lib knows.
    const options = {
      timeline,
      fill: 'both',
      easing: 'linear'
    } as unknown as KeyframeAnimationOptions;
    return track.animate(headsKeyframes(overflow), options);
  } catch {
    return null;
  }
};

/** The columns' overflow changed (resize, a day added or removed): move the animation's end. */
export const retargetRide = (ride: Animation, overflow: number): void => {
  const effect = ride.effect as
    | (KeyframeEffect & { setKeyframes?: (k: Keyframe[]) => void })
    | null;
  effect?.setKeyframes?.(headsKeyframes(overflow));
};

const TournamentDayCarousel: React.FC<TournamentDayCarouselProps> = ({
  days,
  tournaments,
  onSelectTournament,
  restrictedIds,
  size = 'large',
  emptyCopy = 'Whoa, partner. You filtered too much. It\u2019s looking mighty empty here.',
  todayKey,
  initialDay = '',
  onDayChange
}) => {
  // The chips scroll inside their GROUND, not the row (see .dayGround), so
  // that is what mouse-drag attaches to; the row carries the sticky pin and
  // the collapse.
  const dayGroundRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const headTrackRef = useRef<HTMLDivElement>(null);

  // The chips' row collapses on scroll like the club-stats row (owner,
  // 2026-09-11).
  const setDayRowCollapse = useCollapsingRow({ vars: DAY_ROW_VARS, extent: dayGroundOf });

  const byDay = useMemo(() => {
    const map = new Map<string, Tournament[]>();
    for (const t of tournaments) {
      const list = map.get(t.dayKey) ?? [];
      list.push(t);
      map.set(t.dayKey, list);
    }
    // Within each day: pure ascending startAt — nearest to start on top,
    // regardless of status or registration. See compareTournamentsInDay.
    map.forEach((list) => {
      list.sort(compareTournamentsInDay);
    });
    return map;
  }, [tournaments]);

  // A day with nothing to play is not a day worth showing (owner, 2026-09-10):
  // days with zero tournaments after filtering are dropped from BOTH the day
  // selector and the carousel, always. This used to be the opt-in "Hide Empty
  // Days" filter — the schedule runs a fortnight out and most of those days are
  // empty, so the option only ever offered a row of columns saying "nothing
  // here". Every day that renders therefore has at least one tournament in it.
  const visibleDays = useMemo(
    () => days.filter((d) => (byDay.get(d.dayKey)?.length ?? 0) > 0),
    [days, byDay]
  );

  // Re-attach when the columns come back. This component early-returns an
  // empty state when nothing matches the filters, and BOTH scrollers are gone
  // from the tree while it shows — so the one-shot attach would read two null
  // refs and never run again. Narrowing the type chips to nothing and widening
  // them again left mouse drag dead for the rest of the mount; touch and wheel
  // kept working, which is what made it silent (review, 2026-09-13).
  useDragScroll(dayGroundRef, { reattachKey: visibleDays.length > 0 });
  useDragScroll(carouselRef, { reattachKey: visibleDays.length > 0 });

  const [activeDay, setActiveDay] = useState<string>(initialDay);

  /** The columns themselves — stable across a poll that changes nothing. */
  const dayKeySig = visibleDays.map((d) => d.dayKey).join();

  // Keep the lobby's remembered day in step with every change made here — the
  // seed/re-seed below as well as chip clicks — so returning to the tab can't
  // restore a day that was silently replaced.
  const selectDay = useCallback(
    (dayKey: string) => {
      setActiveDay(dayKey);
      onDayChange?.(dayKey);
    },
    [onDayChange]
  );

  // Seed the active day to the first visible day when data first arrives, and
  // re-seed whenever the selected day is no longer visible — e.g. a filter
  // empties the active day so it drops out of the list, or the day the
  // player was last on has rolled off the schedule since they left the tab — so
  // the selection never points at a hidden column.
  useEffect(() => {
    if (visibleDays.length === 0) return;
    if (visibleDays.some((d) => d.dayKey === activeDay)) return;
    // FIRST seed vs a day going out from under the player. Only the first may
    // move the viewport (JRam, 2026-09-14). Hiding empty days is unconditional
    // now, so `visibleDays` shrinks on an ordinary poll — a day whose last
    // tournament leaves the filtered list disappears with no player action at
    // all. Re-seeding the selection is still right, the column it pointed at is
    // gone; hard-scrolling to the start is not, and would yank a player reading
    // Thursday back to day one mid-scroll with no animation and no explanation.
    const firstSeed = !activeDay;
    selectDay(visibleDays[0].dayKey);
    if (firstSeed) carouselRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [visibleDays, activeDay, selectDay]);

  const scrollToDay = (dayKey: string, behavior: ScrollBehavior = 'smooth') => {
    const el = carouselRef.current;
    const node = el?.querySelector<HTMLDivElement>(`[data-day="${dayKey}"]`);
    if (!node || !el) return;
    const elBox = el.getBoundingClientRect();
    el.scrollTo({
      left: dayScrollLeft({
        scrollLeft: el.scrollLeft,
        laneLeft: elBox.left,
        dayLeft: node.getBoundingClientRect().left,
        padLeft: parseFloat(getComputedStyle(el).paddingLeft) || 0
      }),
      behavior
    });
  };

  // Bring the carousel to the restored day once, on mount. Instantly, not
  // smoothly: the tab is only just appearing, so the chips and the column in view
  // have to already agree — animating there would look like a stray scroll.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !activeDay || visibleDays.length === 0) return;
    restoredRef.current = true;
    scrollToDay(activeDay, 'auto');
  }, [activeDay, visibleDays]);

  const handleSelect = (dayKey: string) => {
    selectDay(dayKey);
    scrollToDay(dayKey);
  };

  // Keep the heads over their columns. The carousel is the only scroller. Where
  // the browser has ScrollTimeline the heads' track rides it on the compositor,
  // frame for frame with the tiles (rideScrollTimeline); everywhere else, and
  // wherever a ride turns out not to follow the scroll, the track mirrors
  // scrollLeft into its transform from a passive listener — one way, a frame
  // late, never through React state and never writing into the carousel. The
  // ride is checked twice — after it starts (a track parked at its end keyframe
  // is the Safari failure) and after the first scroll (a track that never
  // moves) — and only once the scroll has come to REST: the timeline advances
  // a frame behind scrollLeft, so a check mid-flight (a chip tap is a smooth,
  // animated scroll) would read a lag as a fault and demote a healthy browser.
  // Runs again when the columns change — their number or width changes the
  // overflow without changing the carousel's box, which is all a
  // ResizeObserver sees.
  useEffect(() => {
    const lane = carouselRef.current;
    const track = headTrackRef.current;
    if (!lane || !track) return undefined;
    let disposed = false;
    let raf = 0;
    let ride: Animation | null = null;
    let mirror: (() => void) | null = null;
    const overflow = () => Math.max(0, lane.scrollWidth - lane.clientWidth);

    const startMirror = () => {
      if (mirror) return;
      ride?.cancel();
      ride = null;
      mirror = () => {
        track.style.transform = headsShiftFor(lane.scrollLeft);
      };
      lane.addEventListener('scroll', mirror, { passive: true });
      mirror();
    };
    // Wait for a frame in which scrollLeft did not change, then judge the ride
    // against it; give up quietly if the scroll never rests within the budget.
    const verifyWhenSettled = (framesLeft: number) => {
      const before = lane.scrollLeft;
      raf = requestAnimationFrame(() => {
        if (disposed || !ride) return;
        if (lane.scrollLeft !== before) {
          if (framesLeft > 0) verifyWhenSettled(framesLeft - 1);
          return;
        }
        // Re-measure before demoting. `overflow()` was read once, at effect
        // time; if the lane had no width yet the ride was built against 0 and
        // is out of step through no fault of the timeline. Retarget, look
        // again, and only then fall back (review, 2026-09-13).
        if (!headsInStep(track, lane)) {
          retargetRide(ride, overflow());
          if (!headsInStep(track, lane)) startMirror();
        }
      });
    };
    const onFirstScroll = () => {
      lane.removeEventListener('scroll', onFirstScroll);
      if (ride) verifyWhenSettled(60);
    };

    ride = rideScrollTimeline(track, lane, overflow());
    if (ride) {
      lane.addEventListener('scroll', onFirstScroll, { passive: true });
      raf = requestAnimationFrame(() => verifyWhenSettled(60));
    } else {
      startMirror();
    }
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (ride) retargetRide(ride, overflow());
          })
        : null;
    ro?.observe(lane);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      ro?.disconnect();
      lane.removeEventListener('scroll', onFirstScroll);
      if (mirror) lane.removeEventListener('scroll', mirror);
      ride?.cancel();
      track.style.transform = '';
    };
    // Keyed on the columns themselves, not on `visibleDays`' identity: that
    // array is rebuilt whenever `tournaments` is, i.e. on every poll of a live
    // lobby, and each rebuild cancelled the animation, cleared the transform,
    // constructed a new ScrollTimeline and restarted the verify chain —
    // compositor churn on the one path this is protecting, and a possible
    // flicker if a refresh landed mid-fling (review, 2026-09-13).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKeySig, size]);

  // Nothing left to show: either the room is quiet or the filters are too
  // tight, and the lobby is the one that can tell those apart.
  if (visibleDays.length === 0) {
    return <div className={styles.emptyState}>{emptyCopy}</div>;
  }

  return (
    <>
      <div className={styles.dayRow} ref={setDayRowCollapse}>
        {/* The ground is what collapses (opaque, translated up under the panel
            as the list scrolls) and what SCROLLS the chips sideways — see
            .dayGround; the chips sit on their own track inside it so they can
            centre in the row (.dayTrack). The row carries the pin and the clip. */}
        <div className={styles.dayGround} ref={dayGroundRef}>
          <div className={styles.dayTrack}>
            {visibleDays.map((d) => {
              const count = byDay.get(d.dayKey)?.length ?? 0;
              const isActive = activeDay === d.dayKey;
              const isToday = todayKey === d.dayKey;
              return (
                <button
                  type='button'
                  key={d.dayKey}
                  // Today and selected are different facts and can be true at
                  // once; the class order lets the selected treatment win the
                  // border while `aria-current` still says which day is today.
                  className={`${styles.dayChip} ${isToday ? styles.dayChipToday : ''} ${
                    isActive ? styles.dayChipActive : ''
                  }`}
                  aria-current={isToday ? 'date' : undefined}
                  // …and SELECTED is the other half of that pair. It is the
                  // only thing a tap actually changes, and it reached the class
                  // name and nothing else: a screen-reader user tapped a chip
                  // and got no confirmation that anything had happened, while
                  // the one chip that WAS announced was announced for an
                  // unrelated reason. Every other selectable control in this
                  // lobby exposes it — TypeRow's pills, the drawer's toggles,
                  // Your Tables' fans — and the newest were the exception
                  // (round 5, 2026-09-13).
                  aria-pressed={isActive}
                  onClick={() => handleSelect(d.dayKey)}>
                  <span className={styles.dayChipName}>{d.dayName}</span>
                  <span className={styles.dayChipDate}>{d.dayDate}</span>
                  <span
                    className={`${styles.dayChipCount} ${
                      isActive ? styles.dayChipCountActive : ''
                    }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* THE COLUMN HEADS, pinned (owner, 2026-09-11): a lane of their own above
          the columns, because a head inside the sideways-scrolling carousel
          could only stick to the carousel's box. Their track is moved with the
          columns on the carousel's own scroll timeline (rideScrollTimeline, and
          .headTrack in the stylesheet), laid out on the columns' geometry
          (.headCol), and each head labels its
          column for assistive tech, which is what keeps "Thursday" attached to
          Thursday's tournaments in the tree even though the two are siblings
          in the DOM. */}
      <div className={`${styles.heads} ${size === 'compact' ? styles.headsCompact : ''}`}>
        <div className={styles.headLane}>
          <div className={styles.headTrack} ref={headTrackRef}>
            {visibleDays.map((d) => {
              const count = byDay.get(d.dayKey)?.length ?? 0;
              return (
                <div
                  className={`${styles.headCol} ${styles.dayColHeader}`}
                  key={d.dayKey}
                  id={dayHeadId(d.dayKey)}>
                  {/* The column's heading (EN-10751), the Tournament variant of
                      the lobby's one section title. "Mon" alone would not do as
                      a rotor entry — the carousel runs three weeks, so there are
                      three of every weekday — so the heading's name carries the
                      date the column already shows beside it, and that visible
                      copy is hidden from the reader rather than announced twice. */}
                  <h2 className={styles.dayColName} aria-label={`${d.dayName} ${d.dayNum}`}>
                    {d.dayName}
                  </h2>
                  <span className={styles.dayColDate} aria-hidden>
                    {d.dayNum}
                  </span>
                  <span className={styles.dayColCount}>
                    {count} {count === 1 ? 'event' : 'events'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className={`${styles.carousel} ${size === 'compact' ? styles.carouselCompact : ''}`}
        ref={carouselRef}>
        {visibleDays.map((d) => {
          const items = byDay.get(d.dayKey) ?? [];
          return (
            <div
              className={styles.dayCol}
              key={d.dayKey}
              data-day={d.dayKey}
              role='group'
              aria-labelledby={dayHeadId(d.dayKey)}>
              <div className={styles.dayColList}>
                {/* No per-day empty state: a day with nothing in it never
                    reaches here — see visibleDays. */}
                {items.map((t) => (
                  <TournamentTile
                    key={t.id}
                    tournament={t}
                    onClick={onSelectTournament}
                    restricted={restrictedIds?.has(t.id) ?? false}
                    size={size}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default TournamentDayCarousel;
